import React from 'react';
import { BadgeCheck, Bell, Calendar, Clock, Key, User } from 'lucide-react';
import type { RoutyBookingConfirmedArtifact } from '../../business/routy/routyArtifacts';

interface BookedCardProps {
  artifact: RoutyBookingConfirmedArtifact;
}

/**
 * Compact "Booked" receipt rendered after confirm_booking succeeds — the card
 * carries the details so Routy's text reply can stay to a single sentence.
 */
const BookedCard: React.FC<BookedCardProps> = ({ artifact }) => {
  const rows: Array<{ icon: React.ComponentType<{ className?: string }>; text: string }> = [
    ...(artifact.dateLabel ? [{ icon: Calendar, text: artifact.dateLabel }] : []),
    ...(artifact.timeLabel ? [{ icon: Clock, text: artifact.timeLabel }] : []),
    ...(artifact.assignedTo ? [{ icon: User, text: `Assigned to ${artifact.assignedTo}` }] : []),
    ...(artifact.notifiedVia ? [{ icon: Bell, text: `Confirmation sent (${artifact.notifiedVia})` }] : []),
    ...(artifact.accessCode ? [{ icon: Key, text: `Gate/access code: ${artifact.accessCode}` }] : []),
  ];

  return (
    <div className="ml-10 rounded-lg bg-surface border border-emerald-200 shadow-sm overflow-hidden max-w-sm">
      <div className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
        <BadgeCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        <div className="min-w-0">
          <span className="text-sm font-semibold text-emerald-900 block">Booked</span>
          {artifact.title && <span className="text-xs text-emerald-700/90 block truncate">{artifact.title}</span>}
        </div>
      </div>
      {rows.length > 0 && (
        <div className="px-4 py-3 space-y-2">
          {rows.map(({ icon: Icon, text }, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-ink-700">
              <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-ink-400" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookedCard;
